import { Component } from 'react';
import { of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { lazyInject } from '../../../context/module';
import { Logger } from '../../../models/logger';
import { ApiConfig } from '../../../services/api-config';
import { LightHttp } from '../../../services/light-http.service';
import { UIContext } from '../../../services/ui-context';
import { OptionalChildrenProps } from '../../../types/react.types';
import { BrowserUtils } from '../../../ui-utils/browser-utils';
import { HtmlEntityTypes } from '../../../utils/icon.utils';
import { Rating } from '../../../utils/request.utils';
import styles from './rating-display.component.module.scss';


interface RatingDisplayComponentProps extends OptionalChildrenProps {
    description: string;
    title: string;
    feature: string;
    rating: Rating;
}

interface RatingDisplayComponentState {
    totalRatings: number;
    currentRating: number;
    currentRatingStr: string;
}

/** Copy for a page nobody has rated yet (owner 2026-10-11: an honest empty state, never a made-up score). */
export const NO_RATINGS_COPY = 'Be the first to rate this tool';

/**
 * Real totals from professorx's GET /ratings/<slug> body ({ feature, rating_1..rating_5 }). A slug that was never rated
 * comes back zeroed, and the SSR page props carry `{ result: 'No rating' }` when the build could not fetch one - both
 * are 0 ratings here. Nothing is defaulted: 0 ratings is 0 ratings (it used to read "0.0 / 5 - 1 reviews").
 */
export function summarizeRating(ratingInfo: Partial<Rating> | null | undefined): RatingDisplayComponentState {
    const count = (n: unknown) => (typeof n === 'number' && n > 0 ? n : 0);
    const counts = [ratingInfo?.rating_1, ratingInfo?.rating_2, ratingInfo?.rating_3, ratingInfo?.rating_4, ratingInfo?.rating_5].map(count);
    const totalRatings = counts.reduce((a, b) => a + b, 0);
    if (!totalRatings) {
        return { totalRatings: 0, currentRating: 0, currentRatingStr: '' };
    }
    const currentRating = parseFloat((counts.reduce((sum, n, i) => sum + (i + 1) * n, 0) / totalRatings).toFixed(1));
    return { totalRatings, currentRating, currentRatingStr: currentRating.toFixed(1) };
}

/** Five stars for a 0-5 average, rounded down to a half star: 4.8 is 4.5 stars, 4.0 is four full ones, 0 is none. */
export function starsFor(currentRating: number): Array<'full' | 'half' | 'empty'> {
    const halves = Math.floor((currentRating || 0) * 2);
    return [0, 1, 2, 3, 4].map(i => (halves >= 2 * i + 2 ? 'full' : halves === 2 * i + 1 ? 'half' : 'empty'));
}


export default class RatingDisplayComponent extends Component<RatingDisplayComponentProps, RatingDisplayComponentState> {
    private readonly logger = Logger.getLogger('RatingDisplayComponent');

    readableFeatureName?: string;

    @lazyInject(LightHttp) private http: LightHttp;
    @lazyInject(UIContext) private uiContext: UIContext;
    @lazyInject(ApiConfig) private apiConfig: ApiConfig;

    constructor(props) {
        super(props);
        this.state = this.generateState(this.props.rating)
    }

    componentDidMount() {
        if (BrowserUtils.hasWindow()) {
            this.loadRatings$().subscribe({
                error: (err) => {
                    this.logger.error({ error: err, message: `Error getting rating.` });
                    this.setUp(this.props.rating);
                }
            });
        } else {
            this.setUp(this.props.rating);
        }
    }

    private setUp(ratingInfo: Rating) {
        this.setState(this.generateState(ratingInfo));
    }

    // The SoftwareApplication / AggregateRating JSON-LD is xconvert-web-ssr's (prerender-urls seoHeaderTagsJson,
    // rendered by _app). This component used to build a second copy into state that nothing rendered; removed in the
    // source-of-truth audit (2026-10-10).
    private generateState(ratingInfo: Rating) {
        this.readableFeatureName = this.props.feature.replace(/-/g, ' ');
        return summarizeRating(ratingInfo);
    }

    rateFeature(rating: number) {
        this.http.put(`${this.apiConfig.getHttpsEndpoint()}/ratings/${this.props.feature}`, {
            value: rating,
        }).subscribe({
            next: (res: any) => {
                if (res.rated) {
                    this.uiContext.successToast('Thank you for rating!');
                    setTimeout(() => {
                        this.loadRatings$().subscribe();
                        this.forceUpdate();
                    }, 500);
                } else {
                    this.uiContext.warnToast('You can only rate a feature once', '', false, false);
                }
            },
            error: err => {
                this.logger.error({ error: err, message: `Error rating feature ${this.props.feature} to ${rating}.` });
            },
        }
        );
    }

    getRatingStars() {
        const stars = starsFor(this.state.currentRating);
        return <div className={`${styles['stars']}`}>
            {stars.map((v, i) => {
                return <span key={`star-${i}`}
                    onClick={(e) => this.rateFeature(i + 1)}
                    dangerouslySetInnerHTML={{ __html: v === 'full' ? HtmlEntityTypes.STAR : (v === 'half' ? HtmlEntityTypes.HALF_START : HtmlEntityTypes.NO_STAR) }}>
                </span>
            })
            }
        </div>
    }

    loadRatings$() {
        return this.http.get<Rating>(`${this.apiConfig.getHttpsEndpoint()}/ratings/${this.props.feature}`).pipe(
            tap((data: Rating) => this.setUp(data)),
            catchError(err => {
                this.logger.error({ error: err, message: `Error getting rating.` });
                return of({});
            }),
        );
    }

    render() {
        return <div className="section" >
            <div className={styles['rating-container']}>
                <h4>Rate {this.props.title} Tool</h4>
                <div>
                    {this.getRatingStars()}
                    <span className="text ms-3">{this.state.totalRatings > 0
                        ? `Rating: ${this.state.currentRatingStr} / 5 - ${this.state.totalRatings} ${this.state.totalRatings === 1 ? 'review' : 'reviews'}`
                        : NO_RATINGS_COPY}</span>
                </div>
            </div >

        </div >
    }
}